import { z } from "zod";
import { isoDateSchema } from "./common.js";

// =============================================================================
// Invoice schemas
//
// Wire shapes for /api/admin/invoices/*. A generated invoice is a FROZEN
// snapshot (see apps/api db schema `invoices` + `invoice_line_items`): the
// header carries the period, the money totals in integer CENTS (fares + IT
// fees + standby = grand total), the standby inputs, and the lifecycle
// status; the line items are the per-trip-leg (+ per-standby-day) snapshot
// captured at generate time so a sent invoice never moves when the TripLink
// sync later re-prices the underlying bookings.
//
// Reused by the admin React Billing page so the types line up with what the
// BFF returns. Follows the conventions in schemas/admin.ts.
// =============================================================================

/** Invoice lifecycle status. 'generated' → 'sent' (billed) → 'paid', plus a
 *  soft 'void' from any non-paid state (delete is void — the number is
 *  always preserved). */
export const invoiceStatusSchema = z.enum([
  "generated",
  "sent",
  "paid",
  "void",
]);
export type InvoiceStatus = z.infer<typeof invoiceStatusSchema>;

/** Line kind — a billed trip leg or a standby-vehicle day. */
export const invoiceLineTypeSchema = z.enum(["trip", "standby"]);
export type InvoiceLineType = z.infer<typeof invoiceLineTypeSchema>;

// ----- DTOs ----------------------------------------------------------------

export const invoiceLineItemSchema = z.object({
  id: z.string().uuid(),
  invoiceId: z.string().uuid(),
  lineType: invoiceLineTypeSchema,
  sortOrder: z.number().int(),
  /** Soft link to the source bookings row (trip lines); null for standby. */
  bookingId: z.string().uuid().nullable(),
  /** TripLink friendly id ("…-A"/"-B"), or the standby sentinel. */
  tripId: z.string(),
  /** Service date (YYYY-MM-DD); null when the booking had none. */
  tripDate: z.string().nullable(),
  costCenter: z.string(),
  callerName: z.string(),
  customerName: z.string(),
  mrn: z.string(),
  notes: z.string(),
  /** Level-of-service label ("MS-Wheelchair", …); blank for standby. */
  los: z.string(),
  pickupAddress: z.string(),
  pickupCity: z.string(),
  pickupZip: z.string(),
  pickupCounty: z.string(),
  dropoffAddress: z.string(),
  dropoffCity: z.string(),
  dropoffZip: z.string(),
  dropoffCounty: z.string(),
  /** Trip distance in miles; null for standby / unmetered lines. */
  mileage: z.number().nullable(),
  /** IT fee in cents; null for standby. */
  itFeeCents: z.number().int().nullable(),
  /** Trip fare in cents ($0 for cancellations); null for standby. */
  fareCents: z.number().int().nullable(),
  /** Hand-set-fare breakdown, frozen with the invoice: the rate the trip would
   *  have billed at, the amount added on top, and why. Null/"" on an ordinary
   *  trip and on standby, so the invoice columns stay blank except on the
   *  exceptions. Defaulted so line items frozen before Aug 2026 (which carry no
   *  breakdown) still parse. */
  contractFareCents: z.number().int().nullable().default(null),
  extraChargeCents: z.number().int().nullable().default(null),
  chargeReason: z.string().default(""),
  /** Line total in cents (fare + IT fee, or the standby amount). */
  totalCents: z.number().int(),
});
export type InvoiceLineItem = z.infer<typeof invoiceLineItemSchema>;

export const invoiceSchema = z.object({
  id: z.string().uuid(),
  invoiceNumber: z.string(),
  client: z.string(),
  seqYear: z.number().int(),
  seqNum: z.number().int(),
  clientName: z.string(),
  periodStart: z.string(),
  periodEnd: z.string(),
  issueDate: z.string(),
  status: invoiceStatusSchema,

  // Frozen money totals (integer cents).
  faresCents: z.number().int().nonnegative(),
  itFeesCents: z.number().int().nonnegative(),
  standbyCents: z.number().int().nonnegative(),
  grandTotalCents: z.number().int().nonnegative(),
  totalMiles: z.number().nonnegative(),

  // Standby inputs (total = vehicles × dailyRateCents × days).
  standbyVehicles: z.number().int().nonnegative(),
  standbyDailyRateCents: z.number().int().nonnegative(),
  standbyDays: z.number().int().nonnegative(),

  // Snapshot trip counts.
  billedTrips: z.number().int().nonnegative(),
  cancelledFeeOnlyTrips: z.number().int().nonnegative(),
  unpricedTrips: z.number().int().nonnegative(),

  notes: z.string(),
  generatedByUserId: z.string().uuid().nullable(),
  /** Display name (or email) of the generator; null when unknown/deleted. */
  generatedByName: z.string().nullable(),
  createdAt: z.string(),
  sentAt: z.string().nullable(),
  paidAt: z.string().nullable(),
  paidNote: z.string(),
  voidedAt: z.string().nullable(),
  voidReason: z.string(),
});
export type Invoice = z.infer<typeof invoiceSchema>;

// ----- Standby input -------------------------------------------------------

/** Standby-vehicles charge inputs. Total = vehicles × dailyRateCents ×
 *  days. `days` is optional on the wire — the server defaults it to the
 *  inclusive length of the [dateFrom, dateTo] period when omitted, matching
 *  the live Billing-page calculator. */
export const invoiceStandbyInputSchema = z.object({
  vehicles: z.number().int().min(0).max(10_000),
  dailyRateCents: z.number().int().min(0).max(100_000_000),
  days: z.number().int().min(1).max(366).optional(),
});
export type InvoiceStandbyInput = z.infer<typeof invoiceStandbyInputSchema>;

// ----- Preview (compute totals + next number, no persistence) --------------

export const invoicePreviewRequestSchema = z
  .object({
    client: z.string().min(1).max(16).default("MSMC"),
    dateFrom: isoDateSchema,
    dateTo: isoDateSchema,
    standby: invoiceStandbyInputSchema,
    /** Same floor the generate dialog offers, so the PREVIEWED number matches
     *  the one generate will actually allocate for the first invoice of a
     *  (client, year). Ignored once a row exists for the (client, year). */
    startingSeq: z.number().int().min(1).max(100_000).optional(),
  })
  .refine((v) => v.dateFrom <= v.dateTo, {
    message: "dateFrom must be on or before dateTo.",
    path: ["dateTo"],
  });
export type InvoicePreviewRequest = z.infer<typeof invoicePreviewRequestSchema>;

export const invoicePreviewResponseSchema = z.object({
  /** The number this invoice WOULD get if generated now. */
  invoiceNumber: z.string(),
  seqYear: z.number().int(),
  seqNum: z.number().int(),
  periodStart: z.string(),
  periodEnd: z.string(),
  faresCents: z.number().int().nonnegative(),
  itFeesCents: z.number().int().nonnegative(),
  standbyCents: z.number().int().nonnegative(),
  grandTotalCents: z.number().int().nonnegative(),
  totalMiles: z.number().nonnegative(),
  standbyDays: z.number().int().nonnegative(),
  billedTrips: z.number().int().nonnegative(),
  cancelledFeeOnlyTrips: z.number().int().nonnegative(),
  unpricedTrips: z.number().int().nonnegative(),
});
export type InvoicePreviewResponse = z.infer<
  typeof invoicePreviewResponseSchema
>;

// ----- Generate ------------------------------------------------------------

export const invoiceGenerateRequestSchema = z
  .object({
    client: z.string().min(1).max(16).default("MSMC"),
    dateFrom: isoDateSchema,
    dateTo: isoDateSchema,
    standby: invoiceStandbyInputSchema,
    /** Optional override for the FIRST portal invoice of a (client, year)
     *  so the series can continue after externally-issued numbers (e.g.
     *  start at 4 to continue MSMC-2026-003). Ignored once a row exists
     *  for the (client, year). */
    startingSeq: z.number().int().min(1).max(100_000).optional(),
    notes: z.string().max(2000).default(""),
  })
  .refine((v) => v.dateFrom <= v.dateTo, {
    message: "dateFrom must be on or before dateTo.",
    path: ["dateTo"],
  });
export type InvoiceGenerateRequest = z.infer<
  typeof invoiceGenerateRequestSchema
>;

export const invoiceGenerateResponseSchema = z.object({
  invoice: invoiceSchema,
});
export type InvoiceGenerateResponse = z.infer<
  typeof invoiceGenerateResponseSchema
>;

// ----- List ----------------------------------------------------------------

export const invoicesListQuerySchema = z.object({
  /** Filter by status. Omit for all (incl. void). */
  status: invoiceStatusSchema.optional(),
  /** Free-text match against invoice number / period. Empty = no filter. */
  search: z.string().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  beforeCreatedAt: z.string().optional(),
  beforeId: z.string().uuid().optional(),
});
export type InvoicesListQuery = z.infer<typeof invoicesListQuerySchema>;

export const invoicesListResponseSchema = z.object({
  invoices: z.array(invoiceSchema),
  hasMore: z.boolean(),
  nextCursor: z
    .object({ createdAt: z.string(), id: z.string().uuid() })
    .nullable(),
  /** Counts by status across the whole table (drives the filter chips). */
  counts: z.object({
    generated: z.number().int().nonnegative(),
    sent: z.number().int().nonnegative(),
    paid: z.number().int().nonnegative(),
    void: z.number().int().nonnegative(),
  }),
});
export type InvoicesListResponse = z.infer<typeof invoicesListResponseSchema>;

// ----- Get (header + line items) -------------------------------------------

export const invoiceGetResponseSchema = z.object({
  invoice: invoiceSchema,
  lineItems: z.array(invoiceLineItemSchema),
});
export type InvoiceGetResponse = z.infer<typeof invoiceGetResponseSchema>;

// ----- Lifecycle transitions -----------------------------------------------

/** Mark sent / billed — no body fields (the actor + time are server-set). */
export const invoiceMarkSentRequestSchema = z.object({}).strict();
export type InvoiceMarkSentRequest = z.infer<
  typeof invoiceMarkSentRequestSchema
>;

export const invoiceMarkPaidRequestSchema = z.object({
  /** Optional explicit paid date (YYYY-MM-DD); server defaults to today. */
  paidAt: isoDateSchema.optional(),
  note: z.string().max(500).default(""),
});
export type InvoiceMarkPaidRequest = z.infer<
  typeof invoiceMarkPaidRequestSchema
>;

export const invoiceVoidRequestSchema = z.object({
  reason: z.string().max(500).default(""),
});
export type InvoiceVoidRequest = z.infer<typeof invoiceVoidRequestSchema>;

/** Shared response for the single-invoice lifecycle mutations (mark sent /
 *  paid / void). */
export const invoiceMutationResponseSchema = z.object({
  invoice: invoiceSchema,
});
export type InvoiceMutationResponse = z.infer<
  typeof invoiceMutationResponseSchema
>;

// ----- Invoice review (READ-ONLY discrepancy report) -----------------------
//
// Wire shapes for `GET /api/admin/invoices/review`. Purely informational:
// the report SURFACES data discrepancies (missing / malformed cost centers
// + MRNs, paired-leg issues, deadhead rate adjustments) over the EXACT same
// trip universe as the invoice, so the Royal/TripLink team can correct them
// BY HAND in TMS. The portal never mutates anything from this report; the
// endpoint is GET-only and the service is select-only.

/** Per-leg discrepancy flag code. `*_missing` are "Need fixing"; the rest are
 *  "Need review". */
export const invoiceReviewFlagCodeSchema = z.enum([
  "cost_center_missing",
  "mrn_missing",
  "cost_center_looks_wrong",
  "mrn_needs_cleanup",
  "paired_leg_has_issues",
]);
export type InvoiceReviewFlagCode = z.infer<typeof invoiceReviewFlagCodeSchema>;

/** Drives the section a row lands in + the fixing-first attention count. */
export const invoiceReviewSeveritySchema = z.enum(["fixing", "review"]);
export type InvoiceReviewSeverity = z.infer<typeof invoiceReviewSeveritySchema>;

export const invoiceReviewFlagSchema = z.object({
  code: invoiceReviewFlagCodeSchema,
  severity: invoiceReviewSeveritySchema,
  /** Human one-liner ("Cost Center missing"). */
  label: z.string(),
  /** Optional extra detail (e.g. the offending value). */
  detail: z.string().default(""),
});
export type InvoiceReviewFlag = z.infer<typeof invoiceReviewFlagSchema>;

/** One flagged trip leg. Carries ALL of the leg's flags; it lands in
 *  `needFixing` when any flag is `fixing`, else in `needReview`. */
export const invoiceReviewRowSchema = z.object({
  bookingId: z.string().uuid(),
  /** TripLink friendly id ("…-A"/"-B"), or "" when missing. */
  tripId: z.string(),
  tripDate: z.string(),
  customerName: z.string(),
  costCenter: z.string(),
  mrn: z.string(),
  flags: z.array(invoiceReviewFlagSchema),
  /** Read-only reviewer hint parsed from the raw trip note ("Cost center found
   *  in note: 7648 — verify and enter in TMS" / "MRN found in note: …").
   *  Emitted only for rows flagged on the matching axis. "" when the note has
   *  no candidate. Display-only — never billed, never written back. */
  hint: z.string().default(""),
});
export type InvoiceReviewRow = z.infer<typeof invoiceReviewRowSchema>;

/** A deadhead / manual fare-override leg, shown informationally. Contract =
 *  the override-free computed fare; Add-on = final − contract; Final = the
 *  hand-set override. All in integer cents; contract/add-on are null when
 *  the un-overridden trip can't be priced (no clock / mileage). */
export const invoiceReviewAdjustmentSchema = z.object({
  bookingId: z.string().uuid(),
  tripId: z.string(),
  tripDate: z.string(),
  customerName: z.string(),
  contractCents: z.number().int().nullable(),
  addonCents: z.number().int().nullable(),
  finalCents: z.number().int(),
  /** Why the fare was hand-set (the required override note). */
  note: z.string().default(""),
  /** Display name (or email) of who set the override; null when the
   *  account was since deleted or the override predates attribution. */
  setBy: z.string().nullable(),
  /** When the override was set (ISO timestamp); null when unknown. */
  setAt: z.string().nullable(),
});
export type InvoiceReviewAdjustment = z.infer<
  typeof invoiceReviewAdjustmentSchema
>;

export const invoiceReviewSummarySchema = z.object({
  periodStart: z.string(),
  periodEnd: z.string(),
  /** Trip counts + money, byte-identical to the invoice/dashboard window
   *  (reused from `reduceInvoiceWindow`). */
  billedTrips: z.number().int().nonnegative(),
  cancelledFeeOnlyTrips: z.number().int().nonnegative(),
  unpricedTrips: z.number().int().nonnegative(),
  faresCents: z.number().int().nonnegative(),
  itFeesCents: z.number().int().nonnegative(),
  /** Transportation total = fares + IT fees, EXCLUDING standby (the page
   *  adds its own standby input to show the grand total). */
  transportationTotalCents: z.number().int().nonnegative(),
  totalMiles: z.number().nonnegative(),
  /** Mutually-exclusive, fixing-first: needFixing + needReview = needAttention. */
  needAttention: z.number().int().nonnegative(),
  needFixing: z.number().int().nonnegative(),
  needReview: z.number().int().nonnegative(),
});
export type InvoiceReviewSummary = z.infer<typeof invoiceReviewSummarySchema>;

export const invoiceReviewQuerySchema = z
  .object({
    dateFrom: isoDateSchema,
    dateTo: isoDateSchema,
  })
  .refine((v) => v.dateFrom <= v.dateTo, {
    message: "dateFrom must be on or before dateTo.",
    path: ["dateTo"],
  });
export type InvoiceReviewQuery = z.infer<typeof invoiceReviewQuerySchema>;

export const invoiceReviewResponseSchema = z.object({
  summary: invoiceReviewSummarySchema,
  needFixing: z.array(invoiceReviewRowSchema),
  needReview: z.array(invoiceReviewRowSchema),
  adjustments: z.array(invoiceReviewAdjustmentSchema),
});
export type InvoiceReviewResponse = z.infer<typeof invoiceReviewResponseSchema>;
